import { useState, useEffect, useCallback } from 'react';
import { B } from '../constants/brand';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Btn, Spinner, ErrorMsg, Card, Badge, Modal, ModalFooter, Input, Select, useToast, Icon } from '../components/ui';
import { AssistantFeedbackPanel } from '../components/shared/AssistantFeedbackPanel';
import { BrandAssetsPanel } from '../components/shared/BrandAssetsPanel';
import { PlaybooksPanel } from '../components/shared/PlaybooksPanel';
import { AiFeedbackPanel } from '../components/shared/AiFeedbackPanel';

const PROVIDERS = [
  { value: 'anthropic',   label: 'Anthropic (Claude)' },
  { value: 'openai',      label: 'OpenAI (ChatGPT)' },
  { value: 'azureOpenai', label: 'Azure OpenAI (embeddings)' },
  { value: 'voyage',      label: 'Voyage AI (embeddings)' },
];

const DEFAULT_BASE_URLS = ['https://api.anthropic.com', 'https://api.openai.com'];
const PROVIDER_HINTS = {
  anthropic: { baseUrl: 'https://api.anthropic.com (default)', keyEnv: 'e.g. ANTHROPIC_API_KEY', model: "leave blank to use each feature's default" },
  openai:    { baseUrl: 'https://api.openai.com (default)',    keyEnv: 'CHATGPT_API_KEY (used automatically if blank)', model: 'e.g. gpt-5 (blank = gpt-5)' },
};

// Small square icon-style button for the reorder / remove controls.
const MiniBtn = ({ label, onClick, disabled, danger }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    style={{
      width: 26, height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      border: `1px solid ${B.border}`, borderRadius: 6, background: B.surface,
      color: disabled ? B.textLt : (danger ? B.red : B.textMd),
      cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13, lineHeight: 1, padding: 0,
    }}
  >{label}</button>
);

// ── One application area with its ordered fallback list of connections ────────
// The model picker for one connection inside one area. Options come from the
// provider's LIVE model list (GET ai-connections/{id}/models); the first option
// is always "the connection's default", which is what a NULL choice resolves to.
// A stored model the live list no longer offers stays selectable, labelled so —
// silently snapping it to the default would change what the area runs on.
// When the list cannot be read, a text box takes a model id instead.
const ModelPicker = ({ conn, value, modelList, onChange, disabled }) => {
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);
  const fallback = conn?.defaultModel ? `Connection default (${conn.defaultModel})` : "Area default model";
  const style = { fontSize: 12, color: B.text, border: `1px solid ${B.border}`, borderRadius: 6, padding: '4px 6px', background: B.surface, maxWidth: 260 };

  if (!modelList || modelList.loading) {
    return <span style={{ fontSize: 12, color: B.textLt }}>{value || fallback} · loading models…</span>;
  }
  if (!modelList.available) {
    return (
      <input
        value={draft}
        disabled={disabled}
        placeholder={fallback}
        title={modelList.reason || 'The model list could not be read — type a model id'}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => { if ((draft || '') !== (value || '')) onChange(draft.trim() || null); }}
        style={{ ...style, width: 200 }}
      />
    );
  }
  const ids = modelList.models.map(m => m.id);
  return (
    <select
      value={value || ''}
      disabled={disabled}
      onChange={e => onChange(e.target.value || null)}
      style={style}
      aria-label={`Model for ${conn?.name || 'connection'}`}
    >
      <option value="">{fallback}</option>
      {value && !ids.includes(value) && <option value={value}>{value} (not in the provider's list)</option>}
      {modelList.models.map(m => (
        <option key={m.id} value={m.id}>{m.label && m.label !== m.id ? `${m.label} — ${m.id}` : m.id}</option>
      ))}
    </select>
  );
};

// The ordered fallback list of connections stored under ONE key — an area's own
// override or a group's setting. `entries` are { connectionID, model }; every
// change hands the whole new list to onSave.
const ConnectionList = ({ entries, connections, connById, onSave, modelLists, supportsAreaModels, emptyText }) => {
  const ids = entries.map(e => e.connectionID);
  const available = connections.filter(c => c.isEnabled && !ids.includes(c.connectionID));

  const move = (i, dir) => {
    const next = [...entries];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onSave(next);
  };
  const remove   = (id) => onSave(entries.filter(e => e.connectionID !== id));
  const add      = (id) => { if (id) onSave([...entries, { connectionID: id, model: null }]); };
  const setModel = (i, model) => onSave(entries.map((e, k) => (k === i ? { ...e, model } : e)));

  return (
    <>
      {entries.length === 0 ? (
        <div style={{ fontSize: 13, color: B.textMd, background: B.surfaceMuted, borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
          <Icon name="info" size={13} /> {emptyText}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
          {entries.map((e, i) => {
            const c = connById[e.connectionID];
            return (
              <div key={e.connectionID} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', border: `1px solid ${B.border}`, borderRadius: 8, background: B.surfaceMuted, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: B.textLt, width: 18 }}>{i + 1}</span>
                <span style={{ fontSize: 13, color: B.text, fontWeight: 600 }}>{c ? c.name : 'Unknown connection'}</span>
                {c && !c.isEnabled && <Badge color={B.textLt}>Disabled</Badge>}
                {c && !c.hasKey && <Badge color={B.red}>No key</Badge>}
                {c && supportsAreaModels && (
                  <ModelPicker conn={c} value={e.model || null} modelList={modelLists[e.connectionID]} onChange={m => setModel(i, m)} />
                )}
                <span style={{ flex: 1 }} />
                <MiniBtn label="↑" onClick={() => move(i, -1)} disabled={i === 0} />
                <MiniBtn label="↓" onClick={() => move(i, 1)} disabled={i === entries.length - 1} />
                <MiniBtn label="✕" onClick={() => remove(e.connectionID)} danger />
              </div>
            );
          })}
        </div>
      )}

      {available.length > 0 && (
        <select
          value=""
          onChange={ev => add(ev.target.value)}
          style={{
            fontSize: 13, color: B.textMd, border: `1px dashed ${B.border}`, borderRadius: 8,
            padding: '7px 10px', background: B.surface, cursor: 'pointer', maxWidth: 320, width: '100%',
          }}
        >
          <option value="">+ Add a connection…</option>
          {available.map(c => <option key={c.connectionID} value={c.connectionID}>{c.name}</option>)}
        </select>
      )}
    </>
  );
};

const entriesOf = (row) => (row?.connectionIDs || []).map((id, i) => ({ connectionID: id, model: (row.models || [])[i] || null }));

// One area inside a group: either follows the group (the normal state) or
// carries its own list, which then wins WHOLE — the group's list is not tried
// behind it. "Use group setting" clears the override.
const GroupAreaRow = ({ feature, groupEntries, listProps, onSaveFeature }) => {
  const overridden = !!feature.overridden;
  // An override starts as a copy of the group's list (or, with no group list,
  // the first enabled connection) — an empty list would save as "no override".
  const firstEnabled = listProps.connections.find(c => c.isEnabled);
  const seed = groupEntries.length ? groupEntries : (firstEnabled ? [{ connectionID: firstEnabled.connectionID, model: null }] : []);
  return (
    <div style={{ borderTop: `1px solid ${B.border}`, padding: '10px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: B.text }}>{feature.label}</span>
        {overridden ? <Badge color={B.amber}>Own setting</Badge> : <Badge color={B.textLt}>Uses group</Badge>}
        <span style={{ fontSize: 11, color: B.textLt }}>default model <code style={{ fontFamily: 'monospace' }}>{feature.defaultModel}</code></span>
        <span style={{ flex: 1 }} />
        {overridden
          ? <Btn size="sm" variant="ghost" onClick={() => onSaveFeature(feature.key, [])}>Use group setting</Btn>
          : <Btn size="sm" variant="ghost" onClick={() => onSaveFeature(feature.key, seed)} disabled={!seed.length}
              title="Give this area its own connections, starting from the group's">Override</Btn>}
      </div>
      {overridden && (
        <div style={{ marginTop: 8, paddingLeft: 12 }}>
          <ConnectionList {...listProps} entries={entriesOf(feature)} onSave={next => onSaveFeature(feature.key, next)}
            emptyText="No connections — this area uses its group's setting." />
        </div>
      )}
    </div>
  );
};

const GroupCard = ({ group, features, listProps, onSaveGroup, onSaveFeature }) => {
  const overrides = features.filter(f => f.overridden).length;
  const [open, setOpen] = useState(overrides > 0);
  const groupEntries = entriesOf(group);
  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: B.text }}>{group.label}</div>
      <div style={{ fontSize: 12, color: B.textMd, margin: '2px 0 10px' }}>{group.description}</div>
      <ConnectionList {...listProps} entries={groupEntries} onSave={next => onSaveGroup(group.key, next)}
        emptyText="No connection set — these areas use the default environment key." />
      {groupEntries.length > 0 && listProps.supportsAreaModels && (
        <div style={{ fontSize: 11, color: B.textLt, marginTop: 6 }}>
          A model picked here applies to every area below that does not have its own setting. Left at the default, each area keeps its own default model.
        </div>
      )}
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        style={{ marginTop: 10, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: B.textMd }}>
        {open ? '▾' : '▸'} {features.length} area{features.length === 1 ? '' : 's'}{overrides ? ` · ${overrides} with ${overrides === 1 ? 'its' : 'their'} own setting` : ''}
      </button>
      {open && (
        <div style={{ marginTop: 6 }}>
          {features.map(f => (
            <GroupAreaRow key={f.key} feature={f} groupEntries={groupEntries} listProps={listProps} onSaveFeature={onSaveFeature} />
          ))}
        </div>
      )}
    </Card>
  );
};

// ── A single connection row in the bottom list ───────────────────────────────
const ConnectionRow = ({ conn, onEdit, onDelete, onTest, testResult, testing }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: `1px solid ${B.border}`, flexWrap: 'wrap' }}>
    <div style={{ minWidth: 180, flex: 1 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: B.text }}>{conn.name}</div>
      <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>
        {PROVIDERS.find(p => p.value === conn.provider)?.label || conn.provider}
        {conn.defaultModel ? <> · <code style={{ fontFamily: 'monospace' }}>{conn.defaultModel}</code></> : <> · <span style={{ color: B.textLt }}>feature default model</span></>}
      </div>
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
      {conn.hasKey
        ? <Badge color={B.green}>Key: {conn.keySource}</Badge>
        : <Badge color={B.red}>No key</Badge>}
      {!conn.isEnabled && <Badge color={B.textLt}>Disabled</Badge>}
    </div>

    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {testResult && testResult.ok && <span style={{ color: B.green, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="check" size={13} /> OK</span>}
      {testResult && !testResult.ok && <span style={{ color: B.red, fontSize: 12, maxWidth: 220, display: 'inline-flex', alignItems: 'center', gap: 4 }} title={testResult.error}><Icon name="warning" size={13} /> Failed</span>}
      <Btn size="sm" variant="secondary" disabled={testing} onClick={onTest}>{testing ? 'Testing…' : 'Test'}</Btn>
      <Btn size="sm" variant="ghost" onClick={onEdit}>Edit</Btn>
      <Btn size="sm" variant="ghost" onClick={onDelete}>Delete</Btn>
    </div>
  </div>
);

// ── Add / edit connection modal ──────────────────────────────────────────────
const ConnectionModal = ({ conn, onClose, onSaved }) => {
  const { toast } = useToast();
  const isNew = !conn.connectionID;
  const [form, setForm] = useState({
    name:         conn.name || '',
    provider:     conn.provider || 'anthropic',
    defaultModel: conn.defaultModel || '',
    baseUrl:      conn.baseUrl && !DEFAULT_BASE_URLS.includes(conn.baseUrl) ? conn.baseUrl : '',
    apiKey:       '',
    apiKeyEnvVar: conn.apiKeyEnvVar || '',
    isEnabled:    conn.isEnabled !== false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name:         form.name.trim(),
        provider:     form.provider,
        defaultModel: form.defaultModel.trim() || null,
        baseUrl:      form.baseUrl.trim() || null,
        apiKeyEnvVar: form.apiKeyEnvVar.trim() || null,
        isEnabled:    form.isEnabled,
      };
      if (form.apiKey.trim()) payload.apiKey = form.apiKey.trim();
      if (isNew) await api.post('ai-connections', payload);
      else       await api.put(`ai-connections/${conn.connectionID}`, payload);
      toast.success(isNew ? 'Connection added' : 'Connection saved');
      onSaved();
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isNew ? 'Add AI Connection' : 'Edit AI Connection'} onClose={onClose} width={520}>
      <Input label="Name" value={form.name} onChange={v => set('name', v)} placeholder="e.g. Claude (Primary)" required autoFocus />
      <Select label="Provider" value={form.provider} onChange={v => set('provider', v)} options={PROVIDERS} />
      <Input label="Default model" value={form.defaultModel} onChange={v => set('defaultModel', v)} placeholder={(PROVIDER_HINTS[form.provider] || PROVIDER_HINTS.anthropic).model} />
      <Input label="Base URL" value={form.baseUrl} onChange={v => set('baseUrl', v)} placeholder={(PROVIDER_HINTS[form.provider] || {}).baseUrl || 'provider default'} />
      <Input
        label={isNew ? 'API key' : 'API key (leave blank to keep current)'}
        type="password"
        value={form.apiKey}
        onChange={v => set('apiKey', v)}
        placeholder={isNew ? 'paste API key' : '•••• unchanged'}
      />
      <Input label="…or app-setting name holding the key" value={form.apiKeyEnvVar} onChange={v => set('apiKeyEnvVar', v)} placeholder={(PROVIDER_HINTS[form.provider] || PROVIDER_HINTS.anthropic).keyEnv} />
      {form.provider === 'openai' && (
        <div style={{ fontSize: 12, color: B.textMd, margin: '-2px 0 8px', lineHeight: 1.45 }}>
          ChatGPT answers the same requests Claude does, translated. Features that rely on Claude-only tools
          (web search, code execution, the document connector) skip an OpenAI connection and fall through to the next one in their list.
        </div>
      )}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: B.text, margin: '4px 0 8px', cursor: 'pointer' }}>
        <input type="checkbox" checked={form.isEnabled} onChange={e => set('isEnabled', e.target.checked)} />
        Enabled
      </label>
      <div style={{ fontSize: 12, color: B.textMd, background: B.surfaceMuted, borderRadius: 6, padding: '8px 12px' }}>
        <Icon name="info" size={12} /> If no key and no app-setting name are given, this connection falls back to the shared <code style={{ fontFamily: 'monospace' }}>ANTHROPIC_API_KEY</code>. Keys are stored write-only and never shown again.
      </div>
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
        <Btn variant="primary" onClick={save} loading={saving}>{isNew ? 'Add Connection' : 'Save'}</Btn>
      </ModalFooter>
    </Modal>
  );
};

export const AIConnections = () => {
  const { toast } = useToast();
  const { data, loading, error, reload } = useApi(() => api.get('ai-connections'), []);
  const [editConn, setEditConn]   = useState(null); // connection object, {} for new, or null
  const [tests, setTests]         = useState({});   // connectionID -> { ok, error }
  const [testingId, setTestingId] = useState(null);
  const [modelLists, setModelLists] = useState({}); // connectionID -> { loading, available, models, reason }

  // Fetch the live model list once for every connection some area uses.
  const usedIds = [...new Set([...(data?.features || []), ...(data?.groups || [])].flatMap(f => f.connectionIDs || []))].sort().join(',');
  const loadModels = useCallback(async (id, refresh = false) => {
    setModelLists(m => ({ ...m, [id]: { ...(m[id] || {}), loading: true } }));
    try {
      const res = await api.get(`ai-connections/${id}/models${refresh ? '?refresh=1' : ''}`);
      setModelLists(m => ({ ...m, [id]: { loading: false, available: !!res.available, models: res.models || [], reason: res.reason } }));
    } catch (e) {
      setModelLists(m => ({ ...m, [id]: { loading: false, available: false, models: [], reason: e.message } }));
    }
  }, []);
  useEffect(() => {
    if (!data?.supportsAreaModels) return;
    for (const id of usedIds ? usedIds.split(',') : []) {
      if (!modelLists[id]) loadModels(id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usedIds, data?.supportsAreaModels, loadModels]);

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

  const connections = data?.connections || [];
  const features    = data?.features || [];
  const connById    = Object.fromEntries(connections.map(c => [c.connectionID, c]));
  const groups      = data?.groups || [];

  const saveList = async (path, entries) => {
    try {
      const body = data?.supportsAreaModels
        ? { connections: entries }
        : { connectionIDs: entries.map(e => e.connectionID) };
      await api.put(path, body);
      reload();
    } catch (e) { toast.error(e.message); }
  };
  const saveFeature = (key, entries) => saveList(`ai-connections/features/${key}`, entries);
  const saveGroup   = (key, entries) => saveList(`ai-connections/groups/${key}`, entries);

  const listProps = { connections, connById, modelLists, supportsAreaModels: !!data?.supportsAreaModels };
  const groupKeys = new Set(groups.map(g => g.key));
  // An area with no (known) group still has to be configurable — shown alone.
  const ungrouped = features.filter(f => !groupKeys.has(f.group));

  const removeConnection = async (conn) => {
    if (!window.confirm(`Delete connection "${conn.name}"? It will be removed from every area's fallback list.`)) return;
    try {
      await api.del(`ai-connections/${conn.connectionID}`);
      toast.success('Connection deleted');
      reload();
    } catch (e) { toast.error(e.message); }
  };

  const testConnection = async (conn) => {
    setTestingId(conn.connectionID);
    setTests(t => ({ ...t, [conn.connectionID]: null }));
    try {
      const res = await api.post(`ai-connections/${conn.connectionID}/test`, {});
      setTests(t => ({ ...t, [conn.connectionID]: res }));
    } catch (e) {
      setTests(t => ({ ...t, [conn.connectionID]: { ok: false, error: e.message } }));
    } finally { setTestingId(null); }
  };

  return (
    <div>
      {data?.needsMigration && (
        <Card style={{ marginBottom: 20, borderColor: B.amber }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: B.text, marginBottom: 4 }}>Setup required</div>
          <div style={{ fontSize: 13, color: B.textMd }}>
            The AI Connections tables haven't been created yet. Run migrations from
            the <b>Payment Generation</b> tab (Run Migrations), then reload this page.
          </div>
        </Card>
      )}

      {/* Application Areas, by group */}
      <div style={{ fontSize: 16, fontWeight: 700, color: B.text, marginBottom: 4 }}>Application Areas</div>
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 14 }}>
        Areas are grouped by the kind of model they need. Set the connection(s) once per group, in priority order —
        if the first fails, the next is tried — and the model each runs{data?.supportsAreaModels ? '' : ' (needs migration 220)'}.
        Open a group to give a single area its own setting; that area then ignores the group's list.
        Nothing set means the default environment key.
      </div>
      {groups.map(g => (
        <GroupCard key={g.key} group={g} features={features.filter(f => f.group === g.key)}
          listProps={listProps} onSaveGroup={saveGroup} onSaveFeature={saveFeature} />
      ))}
      {ungrouped.length > 0 && (
        <Card style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: B.text, marginBottom: 6 }}>Other areas</div>
          {ungrouped.map(f => (
            <div key={f.key} style={{ borderTop: `1px solid ${B.border}`, padding: '10px 0' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: B.text, marginBottom: 8 }}>{f.label}</div>
              <ConnectionList {...listProps} entries={entriesOf(f)} onSave={next => saveFeature(f.key, next)}
                emptyText="Uses the default environment key — no override configured." />
            </div>
          ))}
        </Card>
      )}

      {/* AI Connections */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '28px 0 4px' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: B.text }}>AI Connections</div>
        <Btn size="sm" variant="primary" onClick={() => setEditConn({})}><Icon name="add" size={14} /> Add Connection</Btn>
      </div>
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 14 }}>
        Providers Harvus can talk to. Add another to make it selectable above.
      </div>
      <Card style={{ padding: 0 }}>
        {connections.length === 0 ? (
          <div style={{ padding: 20, fontSize: 13, color: B.textMd }}>No connections yet — add one to get started.</div>
        ) : connections.map(c => (
          <ConnectionRow
            key={c.connectionID}
            conn={c}
            onEdit={() => setEditConn(c)}
            onDelete={() => removeConnection(c)}
            onTest={() => testConnection(c)}
            testResult={tests[c.connectionID]}
            testing={testingId === c.connectionID}
          />
        ))}
      </Card>

      {editConn && (
        <ConnectionModal conn={editConn} onClose={() => setEditConn(null)} onSaved={reload} />
      )}

      <PlaybooksPanel />
      <BrandAssetsPanel />
      <AssistantFeedbackPanel />
      <AiFeedbackPanel />
    </div>
  );
};
